import React, { useMemo, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Grid,
} from "@mui/material";
import { blue } from "@mui/material/colors";
import Seo from "./components/Seo";
import seoRoutes from "../seo/seoRoutes";

// ------- Sample Fee Data (edit amounts as needed) -------
const FEES = {
  "Class 11": [
    {
      stream: "Science",
      heads: {
        Tuition: 9000,
        Admission: 1500,
        Lab: 0,
        Library: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
    {
      stream: "Commerce",
      heads: {
        Tuition: 6000,
        Admission: 1500,
        Library: 0,
        Lab: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
    {
      stream: "Arts",
      heads: {
        Tuition: 6000,
        Admission: 1500,
        Library: 0,
        Lab: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
  ],
  "Class 12": [
    {
      stream: "Science",
      heads: {
        Tuition: 9000,
        Lab: 0,
        Library: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
    {
      stream: "Commerce",
      heads: {
        Tuition: 6000,
        Library: 0,
        Lab: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
    {
      stream: "Arts",
      heads: {
        Tuition: 6000,
        Library: 0,
        Lab: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
  ],
};

// ------- helpers -------
const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);

// Show individual head values
const formatHeadDisplay = (raw) => {
  if (raw === undefined || raw === null) return "—";
  if (typeof raw === "string" && raw.toLowerCase() === "as per jac") return "As Per JAC";

  const n = Number(raw);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "Included";
  return formatINR(n);
};

// ✅ Total: ignore non-numeric items (like "As Per JAC")
const computeTotal = (heads) =>
  Object.values(heads).reduce((sum, v) => {
    const n = Number(v);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

// ✅ Get total fee for a class + stream (returns number)
const getStreamTotal = (className, streamName) => {
  const list = FEES[className] || [];
  const row = list.find((x) => x.stream === streamName);
  if (!row) return 0;
  return computeTotal(row.heads);
};

// ✅ Display total as "₹xx,xxx/-" (matches your current UI)
const formatFeeLabel = (n) => {
  // If you want "/-" formatting:
  const val = formatINR(n).replace("₹", "₹");
  return `${val}/-`;
};

const AdmissionPage = () => {
  const year = new Date().getFullYear();
  const [selectedTab, setSelectedTab] = useState(0); // 0 => Class 11, 1 => Class 12
  const handleTabChange = (_e, v) => setSelectedTab(v);

  const activeClass = selectedTab === 0 ? "Class 11" : "Class 12";

  // ✅ compute once per tab change
  const feesByStream = useMemo(() => {
    return {
      Science: getStreamTotal(activeClass, "Science"),
      Commerce: getStreamTotal(activeClass, "Commerce"),
      Arts: getStreamTotal(activeClass, "Arts"),
    };
  }, [activeClass]);

  // ✅ Subject groups now automatically show correct fee based on selected class
  const scienceData = useMemo(
    () => [
      {
        title: "PCMB",
        description: ["Physics", "Chemistry", "Mathematics", "Biology"],
        fee: formatFeeLabel(feesByStream.Science),
      },
      {
        title: "PCMC",
        description: ["Physics", "Chemistry", "Mathematics", "Computer Science"],
        fee: formatFeeLabel(feesByStream.Science),
      },
      {
        title: "PCME",
        description: ["Physics", "Chemistry", "Mathematics", "Economics"],
        fee: formatFeeLabel(feesByStream.Science),
      },
    ],
    [feesByStream.Science]
  );

  const commerceData = useMemo(
    () => [
      {
        title: "CAMS",
        description: ["Business Studies", "Accountancy", "Mathematics", "Statistics"],
        fee: formatFeeLabel(feesByStream.Commerce),
      },
      {
        title: "CAME",
        description: ["Business Studies", "Accountancy", "Mathematics", "Economics"],
        fee: formatFeeLabel(feesByStream.Commerce),
      },
    ],
    [feesByStream.Commerce]
  );

  const artsData = useMemo(
    () => [
      {
        title: "HESP",
        description: ["History", "Economics", "Sociology", "Political Science"],
        fee: formatFeeLabel(feesByStream.Arts),
      },
      {
        title: "HSEP",
        description: ["History", "Sociology", "Economics", "Political Science"],
        fee: formatFeeLabel(feesByStream.Arts),
      },
      {
        title: "EHSP",
        description: ["Economics", "History", "Sociology", "Political Science"],
        fee: formatFeeLabel(feesByStream.Arts),
      },
    ],
    [feesByStream.Arts]
  );

 const seo = seoRoutes["/admission"];

return (
  <>
    <Seo
      title={seo.title}
      description={seo.description}
      path="/admission"
      image={seo.image}
    />
    <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: "background.default" }}>
      <Container maxWidth="lg">
        <Typography
          align="center"
          sx={{
            fontWeight: 800,
            fontSize: { xs: 22, sm: 26, md: 32 },
            mb: { xs: 1, md: 1.5 },
          }}
        >
          Course Fee
        </Typography>

        <Typography
          align="center"
          sx={{
            fontSize: { xs: 14, sm: 15 },
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          Course fee for the Academic year {year} - {year+1}
        </Typography>

        <Typography
          align="center"
          sx={{ fontSize: { xs: 13.5, sm: 14.5 }, color: "text.secondary", mt: 2 }}
        >
          Fee structure includes admission charges, prospectus fee, and ID card fee. Other state/boards
          (IGCSE, NIOS, etc.) should contact the office.
        </Typography>

        {/* Responsive Tabs */}
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          sx={{
            mt: { xs: 2, md: 3 },
            "& .MuiTabs-indicator": { height: 3, borderRadius: 2 },
          }}
          aria-label="Course stream tabs"
        >
          <Tab
            label="Science"
            sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 15 } }}
            aria-controls="panel-science"
            id="tab-science"
          />
          <Tab
            label="Commerce"
            sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 15 } }}
            aria-controls="panel-commerce"
            id="tab-commerce"
          />
          <Tab
            label="Arts"
            sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 15 } }}
            aria-controls="panel-arts"
            id="tab-arts"
          />
        </Tabs>

        <TabPanel value={selectedTab} index={0} id="panel-science" labelledby="tab-science">
          <CourseList data={scienceData} />
        </TabPanel>
        <TabPanel value={selectedTab} index={1} id="panel-commerce" labelledby="tab-commerce">
          <CourseList data={commerceData} />
        </TabPanel>
        <TabPanel value={selectedTab} index={2} id="panel-arts" labelledby="tab-arts">
          <CourseList data={artsData} />
        </TabPanel>
      </Container>
    </Box>
    </>
  );
};

const TabPanel = ({ children, value, index, id, labelledby }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={id}
    aria-labelledby={labelledby}
    style={{ width: "100%" }}
  >
    {value === index && <Box sx={{ mt: { xs: 2, md: 3 } }}>{children}</Box>}
  </div>
);

const CourseCard = ({ title, description, fee }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      borderRadius: 3,
      boxShadow: "0 1px 2px rgba(0,0,0,.06), 0 12px 28px rgba(0,0,0,.08)",
      border: (t) => `1px solid ${t.palette.divider}`,
    }}
  >
    <CardContent>
      <Typography
        sx={{
          fontWeight: 800,
          textAlign: "center",
          mb: 1,
          color: blue[700],
          fontSize: { xs: 18, sm: 20 },
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: "text.secondary",
          textAlign: "center",
          mb: 1.5,
          fontSize: { xs: 13.5, sm: 14.5 },
        }}
      >
        Fees for the academic year: <strong>{fee}</strong>
      </Typography>

      <Box component="ul" sx={{ m: 0, pl: 3, fontSize: { xs: 13.5, sm: 14.5 }, lineHeight: 1.9 }}>
        {description.map((d, i) => (
          <li key={i}>{d}</li>
        ))}
      </Box>
    </CardContent>
  </Card>
);

const CourseList = ({ data }) => (
  <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
    {data.map((item, idx) => (
      <Grid item key={idx} xs={12} sm={6} md={4}>
        <CourseCard {...item} />
      </Grid>
    ))}
  </Grid>
);

export default AdmissionPage;


// import React, { useState } from "react";
// import {
//   Tabs,
//   Tab,
//   Box,
//   Typography,
//   Card,
//   CardContent,
//   CssBaseline,
// } from "@mui/material";
// import { ThemeProvider, createTheme } from "@mui/material/styles";
// import { blue } from "@mui/material/colors";
// // const theme = createTheme();

// const AdmissionPage = () => {
//   const [selectedTab, setSelectedTab] = useState(0);

//   const handleTabChange = (event, newValue) => {
//     setSelectedTab(newValue);
//   };

//   const scienceData = [
//     {
//       title: "PCMB",
//       description: ["Physics", "Chemistry", "Mathematics", "Biology"],
//       fee: "₹21,000/-",
//     },
//     {
//       title: "PCMC",
//       description: ["Physics", "Chemistry", "Mathematics", "ComputerScience"],
//       fee: "₹21,000/-",
//     },
//     {
//       title: "PCME",
//       description: ["Physics", "Chemistry", "Mathematics", "Economics"],
//       fee: "₹21,000/-",
//     },
//     // Add more Science courses
//   ];

//   const commerceData = [
//     {
//       title: "CAMS",
//       description: [
//         "Commerce (Business Studies)",
//         "Accountancy",
//         "Basic Mathematics",
//         "Statistics",
//       ],
//       fee: "₹12,000/-",
//     },
//     {
//       title: "CAME",
//       description: [
//         "Commerce (Business Studies)",
//         "Accountancy",
//         "Basic Mathematics",
//         "Economics",
//       ],
//       fee: "₹12,000/-",
//     },
//     // Add more Commerce courses
//   ];

//   const artsData = [
//     {
//       title: "HESP",
//       description: ["History", "Economics", "Sociology", " Political Science"],
//       fee: "₹12,000/-",
//     },
//     {
//       title: "HESP",
//       description: ["History", "Economics", "Sociology", " Political Science"],
//       fee: "₹12,000/-",
//     },
//     {
//       title: "HESP",
//       description: ["History", "Economics", "Sociology", " Political Science"],
//       fee: "₹12,000/-",
//     },
//     // Add more Arts courses
//   ];

//   return (
//     // <ThemeProvider theme={theme}>
//     // <CssBaseline />
//     <Box sx={{ backgroundColor: "#f5f5f5", padding: "20px" }}>
//       <Typography
//         variant="h2"
//         align="center"
//         sx={{ marginBottom: { xs: "10px", md: "20px" } }}
//       >
//         Course Fee
//       </Typography>

//       <Typography
//         variant="h5"
//         sx={{ fontSize: 25, fontWeight: 600, marginTop: 5 }}
//         align="center"
//       >
//         Course fee for the Academic year 2023-24
//       </Typography>
//       <Typography
//         variant="h6"
//         align="center"
//         sx={{ fontSize: 20, marginTop: 5 }}
//       >
//         Fees structure has admission charges, Prospectus Fee and Id card Fee .
//       </Typography>

//       <Typography
//         variant="h6"
//         align="center"
//         sx={{ fontSize: 20, marginTop: 5 }}
//       >
//         Other State, Outside India and Other board students (IGCSE, NIOS and
//         Other State Board) should contact the office.
//       </Typography>
//       <Tabs
//         value={selectedTab}
//         onChange={handleTabChange}
//         centered
//         sx={{
//           marginBottom: { xs: "10px", md: "20px" },
//           fontSize: { xs: "1.2rem", md: "2.2rem" },
//         }}
//       >
//         <Tab
//           label={
//             <Typography
//               variant="h6"
//               sx={{
//                 fontSize: { xs: "1.2rem", md: "2.2rem" },
//                 fontWeight: 600,
//               }}
//             >
//               Science
//             </Typography>
//           }
//         />
//         <Tab
//           label={
//             <Typography
//               variant="h6"
//               sx={{
//                 fontSize: { xs: "1.2rem", md: "2.2rem" },
//                 fontWeight: 600,
//               }}
//             >
//               Commerce
//             </Typography>
//           }
//         />
//         <Tab
//           label={
//             <Typography
//               variant="h6"
//               sx={{
//                 fontSize: { xs: "1.2rem", md: "2.2rem" },
//                 fontWeight: 600,
//               }}
//             >
//               Arts
//             </Typography>
//           }
//         />
//       </Tabs>
//       <TabPanel value={selectedTab} index={0}>
//         <CourseList data={scienceData} />
//       </TabPanel>
//       <TabPanel value={selectedTab} index={1}>
//         <CourseList data={commerceData} />
//       </TabPanel>
//       <TabPanel value={selectedTab} index={2}>
//         <CourseList data={artsData} />
//       </TabPanel>
//     </Box>
//     // </ThemeProvider>
//   );
// };

// const TabPanel = ({ children, value, index }) => (
//   <div role="tabpanel" hidden={value !== index}>
//     {value === index && (
//       <Box
//         display="flex"
//         justifyContent="center"
//         alignItems="center"
//         minHeight="100%"
//         mt={5}
//       >
//         {children}
//       </Box>
//     )}
//   </div>
// );

// const CourseCard = ({ title, description, fee }) => (
//   <Card
//     sx={{
//       maxWidth: 300,
//       backgroundColor: "rgba(255, 255, 255, 0.8)", // Transparent background
//       display: "flex",
//       flexDirection: "column",
//       alignItems: "center",
//       justifyContent: "space-between",
//       padding: "20px",
//       margin: 5,
//       boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.2)",
//       transition: "transform 0.2s, box-shadow 0.2s",
//       "&:hover": {
//         transform: "translateY(-10px)",
//         boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
//       },
//     }}
//   >
//     <CardContent>
//       <Typography
//         variant="h6"
//         sx={{
//           fontSize: 25,
//           textAlign: "center",
//           marginBottom: "10px",
//           fontWeight: 600,
//           color: blue[700],
//         }}
//       >
//         {title}
//       </Typography>
//       <Typography
//         variant="body2"
//         color="textSecondary"
//         sx={{ textAlign: "center", fontSize: 20, marginBottom: "10px" }}
//       >
//         Fees for the academic year: {fee}
//       </Typography>
//       <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
//         <ul style={{ paddingInlineStart: "20px", margin: 10 }}>
//           {description.map((course, index) => (
//             <li key={index}>{course}</li>
//           ))}
//         </ul>
//       </Typography>
//     </CardContent>
//   </Card>
// );

// const CourseList = ({ data }) => (
//   <Box display="flex" justifyContent="center" flexWrap="wrap">
//     {data.map((item, index) => (
//       <CourseCard key={index} {...item} />
//     ))}
//   </Box>
// );

// export default AdmissionPage;
